import { useNavigate } from 'react-router-dom'
import { AppPath } from '../enums/AppPath'
import { useState } from 'react'
import { Badge, Card, DataList, Flex, Heading, Separator } from '@radix-ui/themes'

import Credit from '../interfaces/Credit'
import { FormatProps, handleInputChangeFormat, truncateToTwoDecimals } from '../utils/handleInputChangeFormat'
import statusCreditTranslate from '../utils/statusCreditTranslate'
import useCreditPoster from '../hooks/useCreditPoster'
import { useAuth } from '../hooks/useAuth'
import { Rule, validateFields } from '../utils/validateForm'
import { VehicleBasePic } from '../interfaces/Vehicle'
import ClientBase from '../interfaces/ClientBase'
import paymentsCreditTranslate from '../utils/paymentsCreditTranslate'
import { formatCurrency, formatWithThousandSep } from '../utils/utils'
import useCreditFetcher from '../hooks/useCreditFetcher'

import { FormFields, InputFieldProps } from './FormFields'
import VehicleCard from './VehicleCard'
import { ClientCard } from './ClientCard'
import { SelectField } from './SelectField'
import { ButtonAction } from './Buttons'
import { Info } from './MessageInfo'
import InputTextArea from './InputTextArea'
import InputField from './InputField'
import { RadioGroup, RadioItem } from './RadioGroup'
import { DisplayValueVert } from './DisplayValue'
import CreditPreview from './CreditPreview'

interface CreditEx extends Credit {
  amount_format: string
  fee_format: string
  observations_format: string
  // paymentsQty_format: string,
  paymentsAmount_format: string
  rate_format: string
  price_format: string
  salary_format: string
}
export type CreditFormat = Omit<CreditEx, 'vehicle' | 'client'>

const tipoCreditoGroup: RadioItem[] = [{ label: 'Convencional' }, { label: 'Compra inteligente' }]

const CreditEdit: React.FC<{
  vehicle: VehicleBasePic
  client: ClientBase
  creditIn?: CreditFormat
  modificaSw: boolean
  vehicleId: string
  clientId: string
  dealId: string
  onClose?: () => void
}> = ({ vehicle, client, creditIn, modificaSw, vehicleId, clientId, dealId, onClose }) => {
  const iniCredit = creditIn || {
    amount_format: '',
    fee_format: '',
    observations_format: '',
    price_format: '',
    paymentsAmount_format: '',
    rate_format: '',
    _id: '',
    amount: 0,
    fee: 0,
    observations: [],
    paymentsQty: 0,
    paymentsAmount: 0,
    rate: 0,
    status: '',
    deal: dealId,
    institution: '',
    profile: '',
    salary_format: '',
  }

  const { userData } = useAuth()
  const [credit, setCredit] = useState<CreditFormat | null>(iniCredit)

  // Errores de validacion de los input
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
  const { postCredit, errorPostCredit } = useCreditPoster()
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [tipoIdx, setTipoIdx] = useState<number>(credit?.type ?? 0)
  const [preview, setPreview] = useState<boolean>(false)
  const { fetchCreditDetail, errorCredit } = useCreditFetcher()
  const [esModifica, setEsModifica] = useState<boolean>(modificaSw)
  const [esCrea, setEsCrea] = useState<boolean>(false)
  const fields: Array<InputFieldProps> = [
    { label: 'Precio de venta', name: 'price_format', value: credit?.price_format ?? '', type: 'string' },
    { label: 'Monto a financiar', name: 'amount_format', value: credit?.amount_format ?? '', type: 'string' },
  ]
  //     { label: "Cantidad de cuotas", name: "paymentsQty_format", value: credit?.paymentsQty_format ?? "", type: "string" },

  const fields2: Array<InputFieldProps> = [
    {
      label: 'Valor de la cuota',
      name: 'paymentsAmount_format',
      value: credit?.paymentsAmount_format ?? '',
      type: 'string',
    },
    { label: 'Tasa de interés', name: 'rate_format', value: credit?.rate_format ?? '', type: 'string' },
    { label: 'Comisión', name: 'fee_format', value: credit?.fee_format ?? '', type: 'string' },
    { label: 'Sueldo líquido del cliente', name: 'salary_format', value: credit?.salary_format ?? '', type: 'string' },
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setButtonDisabled(false)
    const formatProps: FormatProps[] = [
      { name: 'amount', name_format: 'amount_format' },
      { name: 'fee', name_format: 'fee_format' },
      { name: 'price', name_format: 'price_format' },
      { name: 'paymentsAmount', name_format: 'paymentsAmount_format' },
      { name: 'rate', name_format: 'rate_format', isDecimal: true },
      { name: 'salary', name_format: 'salary_format' },
    ]

    if (!credit) return
    handleInputChangeFormat<CreditFormat>(e, credit, setCredit, formatProps)
    const { name } = e.target

    if (errors?.[name]) {
      errors[name] = ''
    }
  }
  const navigate = useNavigate()

  const handleGoToDeal = () => {
    if (credit?.deal) {
      const dealId = typeof credit.deal === 'object' && credit.deal !== null ? (credit.deal as any)._id : credit.deal
      if (dealId) {
        navigate(`/${AppPath.Deals}/view/${dealId}`, { state: { from: 'credit-edit', creditId: credit?._id } })
      }
    }
  }

      // Modificamos el Credito
    const handleModifyClick = async () => {
      if (!credit) return
  
      if (!userData) return
  
      if (buttonDisabled) return
  
      const isAutofin = credit.source === 'autofin'
  
      const fieldsToValidate = isAutofin
        ? [
            { name: 'status', value: credit.status, rule: Rule.REQUIRED },
            { name: 'institution', value: credit.institution, rule: Rule.REQUIRED },
          ]
        : [
            { name: 'status', value: credit.status, rule: Rule.REQUIRED },
            { name: 'paymentsQty', value: credit.paymentsQty, rule: Rule.REQUIRED },
            { name: 'amount_format', value: credit.amount_format, rule: Rule.GREATER_ZERO },
            //     { name: "fee_format", value: credit.fee_format, rule: Rule.GREATER_ZERO },
            { name: 'institution', value: credit.institution, rule: Rule.REQUIRED },
            { name: 'price_format', value: credit.price_format, rule: Rule.GREATER_ZERO },
            //       { name: "paymentsAmount_format", value: credit.paymentsAmount_format, rule: Rule.GREATER_ZERO },
            //       { name: "rate_format", value: credit.rate_format, rule: Rule.GREATER_ZERO },
            { name: 'salary_format', value: credit.salary_format, rule: Rule.GREATER_ZERO },
          ]
      const validationErrors = validateFields(fieldsToValidate)
  
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
  
        return
      }
      setErrors({})
    // Manipulamos el dato y lo pasamos a Credit

    const observationsArr = credit.observations_format.split('\n')
    const mycredit = {
      _id: credit._id,
      amount: credit.amount,
      fee: credit.fee,
      observations: observationsArr,
      status: credit.status,
      deal: credit.deal,
      institution: credit.institution,
      profile: userData.profile?._id ?? '',
      paymentsQty: credit.paymentsQty,
      paymentsAmount: credit.paymentsAmount,
      rate: credit.rate,
      price: credit.price,
      type: tipoIdx,
      salary: credit.salary,
    }

    const id = esModifica
      ? await postCredit(mycredit, esModifica, '', '', '')
      : await postCredit(mycredit, esModifica, vehicleId, clientId, userData.organization?._id ?? '')

    if (id) {
      setButtonDisabled(true)
      setInfoMessage(esModifica ? 'Solicitud modificada.' : 'Solicitud creada.')

      if (!esModifica) {
        // Estamos creando, y nos fue bien.
        // Lo colocamos en modo modificacion  para activar el preview, etc
        // Pero tenemos que distinguir este caso de la modificacion pura
        // para esto el siguiente EsCrea
        setEsCrea(true)
        setEsModifica(true)
        await creditRead(id)
      }
    }
  }

  // Releemos despues de crear
  const creditRead = async (id: string) => {
    const credit = await fetchCreditDetail(id)

    if (errorCredit || !credit) return

    const amount_format = formatWithThousandSep(credit.amount)
    const fee_format = formatWithThousandSep(credit.fee)
    const observations_format = credit?.observations.join('\n')
    const paymentsQty_format = formatWithThousandSep(credit.paymentsQty ?? 0)
    const paymentsAmount_format = formatWithThousandSep(credit.paymentsAmount ?? 0)
    const rate_format = formatWithThousandSep(credit.rate ?? 0)
    const balance_format = formatWithThousandSep(credit.balance ?? 0)
    const price_format = formatWithThousandSep(credit.price ?? 0)
    const salary_format = formatWithThousandSep(credit.salary ?? 0)

    const creditFormat = {
      ...credit,
      amount_format: amount_format,
      fee_format: fee_format,
      observations_format: observations_format,
      paymentsQty_format: paymentsQty_format,
      paymentsAmount_format: paymentsAmount_format,
      rate_format: rate_format,
      balance_format: balance_format,
      price_format: price_format,
      salary_format: salary_format,
    }
    setCredit(creditFormat)
  }

  const handlePreviewClick = () => {
    setPreview(true)
  }
  const handleGoBack = () => {
    setPreview(false)
  }

  // El porcentaje del credito sobre el precio de venta
  const percentage = (credit?.price ?? 0) > 0 ? (100 * (credit?.amount ?? 0)) / (credit?.price ?? 0) : 0
  const decimalChar = '.'
  const numericValue = percentage.toString()
  const formatted = truncateToTwoDecimals(numericValue, decimalChar).replace('.', ',')

  const getStatusColor = (status: string | undefined): 'green' | 'red' | 'blue' | 'yellow' | 'gray' => {
    if (!status) return 'gray'
    const s = status.toUpperCase()
    if (s.includes('APROBADO') || s.includes('APROBADA')) return 'green'
    if (s.includes('RECHAZADO') || s.includes('RECHAZADA') || s.includes('CANCELADO')) return 'red'
    if (s.includes('PENDIENTE') || s.includes('PROCESO') || s.includes('EVALUACION')) return 'yellow'
    
    return 'blue'
  }

  return (
    <>
      {!preview && (
        <div>
          {(esModifica && !esCrea) ||
            (dealId && (
              <div className="flex flex-col space-y-2  items-center mb-4">
                <div className="flex space-x-4">
                  <div className="border flex items-center justify-center pr-4">
                    {vehicle && <VehicleCard vehicle={vehicle} />}
                  </div>
                  <div className="border flex items-center justify-center pr-4 pl-4">
                    {client && <ClientCard client={client} />}
                  </div>
                </div>
              </div>
            ))}
          <div>
            {/* AUTOFIN VIEW */}            
            {credit?.source === 'autofin' ? (
              <Card className="p-4 bg-gray-50">
                <Flex align="center" gap="4" mb="4">
                  <Heading size="4">Evaluación AutoFin</Heading>
                  <Badge color={getStatusColor(credit.autofinStatus || credit.status)}>
                    {credit.autofinStatus || credit.status}
                  </Badge>
                </Flex>

                <DataList.Root>
                  <Heading size="3" mb="2" mt="2">
                    Datos del Crédito
                  </Heading>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
                    <DataList.Item>
                      <DataList.Label>Institución</DataList.Label>
                      <DataList.Value>{credit.institution}</DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>Precio Vehículo</DataList.Label>
                      <DataList.Value>{formatCurrency(credit.price ?? 0)}</DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>Pie</DataList.Label>
                      <DataList.Value>{formatCurrency(credit.autofinData?.Pie ?? 0)}</DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>Monto a Financiar</DataList.Label>
                      <DataList.Value>{formatCurrency(credit.amount)}</DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>Plazo</DataList.Label>
                      <DataList.Value>{credit.paymentsQty} cuotas</DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>Valor Cuota</DataList.Label>
                      <DataList.Value>{formatCurrency(credit.paymentsAmount ?? 0)}</DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label>CAE</DataList.Label>
                      <DataList.Value>{credit.rate}%</DataList.Value>
                    </DataList.Item>
                    {(credit.fee ?? 0) > 0 && (
                      <DataList.Item>
                        <DataList.Label>Comisión</DataList.Label>
                        <DataList.Value>{formatCurrency(credit.fee ?? 0)}</DataList.Value>
                      </DataList.Item>
                    )}
                    <DataList.Item>
                      <DataList.Label>Tipo</DataList.Label>
                      <DataList.Value>Convencional</DataList.Value>
                    </DataList.Item>
                  </div>

                  {credit.autofinData?.DatosCliente && (
                    <>
                      <Heading size="3" mb="2" mt="4">
                        Datos del Cliente
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
                        <DataList.Item>
                          <DataList.Label>Nombre</DataList.Label>
                          <DataList.Value>
                            {credit.autofinData.DatosCliente.Nombre} {credit.autofinData.DatosCliente.ApPaterno}{' '}
                            {credit.autofinData.DatosCliente.ApMaterno}
                          </DataList.Value>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label>RUT</DataList.Label>
                          <DataList.Value>{credit.autofinData.DatosCliente.Rut}</DataList.Value>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label>Renta</DataList.Label>
                          <DataList.Value>{formatCurrency(credit.autofinData.DatosCliente.Renta ?? 0)}</DataList.Value>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label>Situación Laboral</DataList.Label>
                          <DataList.Value>{credit.autofinData.DatosCliente.SituacionLaboral}</DataList.Value>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label>Tipo Contrato</DataList.Label>
                          <DataList.Value>{credit.autofinData.DatosCliente.TipoContrato}</DataList.Value>
                        </DataList.Item>
                        <DataList.Item>
                          <DataList.Label>Antigüedad</DataList.Label>
                          <DataList.Value>{credit.autofinData.DatosCliente.Antiguedad} meses</DataList.Value>
                        </DataList.Item>
                      </div>
                    </>
                  )}
                </DataList.Root>

                <Separator my="4" size="4" />

                <Heading size="3" mb="2">
                  Datos Editables
                </Heading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormFields
                    fields={[{ label: 'Comisión', name: 'fee_format', value: credit?.fee_format ?? '', type: 'string' }]}
                    handleInputChange={handleInputChange}
                    errors={errors}
                  />
                </div>
              </Card>
            ) : (
              // MANUAL VIEW
              <div className="grid grid-cols-3  gap-4 mt-4">
                <SelectField
                  label="Estado de la Solicitud"
                  name="status"
                  value={credit?.status}
                  options={statusCreditTranslate}
                  noFirstOption={modificaSw}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />

                <InputField
                  label="Institución Financiera"
                  name="institution"
                  value={credit?.institution ?? ''}
                  type="string"
                  handleInputChange={handleInputChange}
                  errors={errors}
                />

                <RadioGroup
                  label="Tipo de crédito"
                  collection={tipoCreditoGroup}
                  name="type"
                  checked={tipoIdx}
                  callback={(idx) => {
                    setTipoIdx(idx)
                    setButtonDisabled(false)
                  }}
                />

                <FormFields fields={fields} handleInputChange={handleInputChange} errors={errors} />
                <DisplayValueVert label="% Monto a financiar" value={formatted} />
                <SelectField
                  label="Cantidad de cuotas"
                  name="paymentsQty"
                  value={credit?.paymentsQty?.toString()}
                  options={paymentsCreditTranslate}
                  noFirstOption={modificaSw}
                  handleInputChange={handleInputChange}
                />

                <FormFields fields={fields2} handleInputChange={handleInputChange} errors={errors} />
              </div>
            )}

            <InputTextArea
              label="Notas"
              name="observations_format"
              value={credit?.observations_format ?? ''}
              handleInputChange={handleInputChange}
            />
            <div className="flex justify-center mt-4 gap-4">
              <ButtonAction
                label={esModifica ? 'Guardar cambios' : 'Crear'}
                handleButtonClick={handleModifyClick}
                disabled={buttonDisabled}
              />
              {credit?.deal && (
                <ButtonAction 
                  label="Ver Negocio" 
                  handleButtonClick={handleGoToDeal} 
                />
              )}
              {esModifica && <ButtonAction label="VistaPrevia" handleButtonClick={handlePreviewClick} />}
              {onClose && <ButtonAction label="Volver al negocio" handleButtonClick={onClose} />}
            </div>
            <div className="flex justify-center mt-4">
              <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} />
            </div>
          </div>
          <div>{errorPostCredit?.message ?? ''}</div>
        </div>
      )}
      {preview && credit && <CreditPreview vehicle={vehicle} client={client} credit={credit} onGoBack={handleGoBack} />}
    </>
  )
}
export default CreditEdit